import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  ClipboardList,
  CreditCard,
  Mail,
  MessageSquare,
  FileText,
  Radio,
  ShieldCheck,
  Video,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useActiveClinic } from '@/lib/activeClinic';
import type { PermissionResource } from '@/lib/permissions';
import { Card } from '@/components/ui/Card';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { SettingsBackLink, SettingsSubpageHeader } from './settingsChrome';

interface IntegrationCard {
  id: string;
  to: string;
  titleKey: string;
  descriptionKey: string;
  icon: typeof CreditCard;
  permissionResource?: PermissionResource;
}

const INTEGRATIONS: IntegrationCard[] = [
  {
    id: 'pay',
    to: '/settings/payments',
    titleKey: 'settings.integrations.cards.pay_title',
    descriptionKey: 'settings.integrations.cards.pay_description',
    icon: CreditCard,
    permissionResource: 'payments',
  },
  {
    id: 'forms',
    to: '/settings/questionnaires',
    titleKey: 'settings.integrations.cards.forms_title',
    descriptionKey: 'settings.integrations.cards.forms_description',
    icon: ClipboardList,
    permissionResource: 'questionnaires',
  },
  {
    id: 'communicate',
    to: '/settings/communicate',
    titleKey: 'settings.integrations.cards.communicate_title',
    descriptionKey: 'settings.integrations.cards.communicate_description',
    icon: Radio,
  },
  {
    id: 'email',
    to: '/settings/email',
    titleKey: 'settings.integrations.cards.email_title',
    descriptionKey: 'settings.integrations.cards.email_description',
    icon: Mail,
  },
  {
    id: 'sms',
    to: '/settings/sms',
    titleKey: 'settings.integrations.cards.sms_title',
    descriptionKey: 'settings.integrations.cards.sms_description',
    icon: MessageSquare,
    permissionResource: 'sms',
  },
  {
    id: 'sygeforsikring',
    to: '/settings/sygeforsikring',
    titleKey: 'settings.integrations.cards.sygeforsikring_title',
    descriptionKey: 'settings.integrations.cards.sygeforsikring_description',
    icon: ShieldCheck,
    permissionResource: 'sygeforsikring',
  },
  {
    id: 'whereby',
    to: '/settings/whereby',
    titleKey: 'settings.integrations.cards.whereby_title',
    descriptionKey: 'settings.integrations.cards.whereby_description',
    icon: Video,
  },
  {
    id: 'patient_reports',
    to: '/settings/patient-reports',
    titleKey: 'settings.integrations.cards.patient_reports_title',
    descriptionKey: 'settings.integrations.cards.patient_reports_description',
    icon: FileText,
  },
];

export function IntegrationsBackLink() {
  return <SettingsBackLink />;
}

export function ClinicIntegrations() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { activeClinicId } = useActiveClinic();

  const visible = INTEGRATIONS.filter((item) => {
    if (!item.permissionResource) return true;
    if (!activeClinicId) return false;
    return hasPermission(activeClinicId, item.permissionResource, 'view');
  });

  return (
    <div className="flex min-w-0 flex-col gap-s4">
      <SettingsSubpageHeader
        title={t('settings.integrations.title')}
        subtitle={t('settings.integrations.subtitle')}
      />

      <div className="grid grid-cols-1 gap-s4 nav:grid-cols-2">
        {visible.map(({ id, to, titleKey, descriptionKey, icon: Icon }) => (
          <Link key={id} to={to} className="min-w-0">
            <Card className="h-full transition-shadow hover:shadow-pop">
              <div className="flex items-start justify-between gap-s3">
                <div className="flex h-10 w-10 items-center justify-center rounded-ctl bg-ds-teal-tint text-ds-teal">
                  <Icon className="h-5 w-5" aria-hidden />
                </div>
                <ArrowRight className="h-4 w-4 text-ds-muted" aria-hidden />
              </div>
              <h3 className={cn(TYPE.section, 'mt-s4 text-ds-text dark:text-white')}>{t(titleKey)}</h3>
              <p className={cn(TYPE.body, 'mt-s1 text-ds-muted')}>{t(descriptionKey)}</p>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
